/* Home page rails: previous/next buttons for the scroll-snap tracks.
   Button states come from IntersectionObservers on the first and last
   pieces, so nothing runs while the page or the rail scrolls. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  document.querySelectorAll('[data-home-rail]').forEach((rail) => {
    const track = rail.querySelector('[data-rail-track]');
    const prev = rail.querySelector('[data-rail-prev]');
    const next = rail.querySelector('[data-rail-next]');
    const items = track ? track.querySelectorAll(':scope > li') : [];
    if (!track || !prev || !next || items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];

    // One page is the content column's width, so a click moves by whole
    // visible pieces and scroll-snap lands on the next one.
    const page = () => {
      const style = getComputedStyle(track);
      const inner = track.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      return Math.max(inner, first.getBoundingClientRect().width);
    };

    const go = (direction) => {
      track.scrollBy({ left: direction * page(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };

    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(1));

    if (!('IntersectionObserver' in window)) {
      prev.disabled = false;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const whole = entry.intersectionRatio > 0.97;
          if (entry.target === first) prev.disabled = whole;
          if (entry.target === last) next.disabled = whole;
        });
      },
      { root: track, threshold: [0, 0.5, 0.97, 1] }
    );

    observer.observe(first);
    if (last !== first) observer.observe(last);
  });
})();

/* Deferred images. Everything below the hero is written with data-src and
   data-srcset (snippets/home-image.liquid, product cards with defer_images).
   Nothing downloads until the hero has loaded (or 2.5 s have passed, or the
   visitor scrolls), so on a slow connection the hero has the line to itself;
   then each image loads as it comes within about a screen of view. Inside the
   horizontal tracks, the visible pieces and the next screenful load first. */
(() => {
  const pending = document.querySelectorAll('[data-home-defer]');
  if (pending.length === 0) return;

  const TRACKS = '.home-rail__track, .home-houses__tiles, .home-trio__list, .home-voices__list';

  // A photo on its way keeps data-home-loading (the card frame shimmers, luxe.css §22);
  // once it lands it fades in, and one that fails leaves the frame plain.
  const landed = (event) => {
    const img = event.currentTarget;
    if (!img.hasAttribute('data-home-loading')) return;
    img.removeAttribute('data-home-loading');
    if (event.type === 'error') {
      img.setAttribute('data-home-failed', '');
      return;
    }
    img.setAttribute('data-home-in', '');
    setTimeout(() => img.removeAttribute('data-home-in'), 600);
  };

  const hydrate = (img) => {
    if (!img.hasAttribute('data-home-defer')) return;
    img.setAttribute('data-home-loading', '');
    img.addEventListener('load', landed);
    img.addEventListener('error', landed);
    img.removeAttribute('data-home-defer');
    const picture = img.parentElement && img.parentElement.tagName === 'PICTURE' ? img.parentElement : null;
    if (picture) {
      picture.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
      });
    }
    img.loading = 'eager';
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
      img.removeAttribute('data-srcset');
    }
    if (img.dataset.src) {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    }
  };

  const watchTrack = (track) => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          entry.target.querySelectorAll('[data-home-defer]').forEach(hydrate);
        });
      },
      { root: track, rootMargin: '0px 100%' }
    );
    [...track.children].forEach((item) => io.observe(item));
  };

  const start = () => {
    if (!('IntersectionObserver' in window)) {
      pending.forEach(hydrate);
      return;
    }
    const near = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          near.unobserve(entry.target);
          if (entry.target.matches(TRACKS)) watchTrack(entry.target);
          else hydrate(entry.target);
        });
      },
      { rootMargin: '700px 0px' }
    );
    const tracks = new Set();
    pending.forEach((img) => {
      const track = img.closest(TRACKS);
      if (track) tracks.add(track);
      else near.observe(img);
    });
    tracks.forEach((track) => near.observe(track));
  };

  let started = false;
  const go = () => {
    if (started) return;
    started = true;
    window.removeEventListener('scroll', go);
    start();
  };

  const hero = document.querySelector('.home-lead__img');
  if (hero && !hero.complete) {
    hero.addEventListener('load', go, { once: true });
    hero.addEventListener('error', go, { once: true });
    window.addEventListener('scroll', go, { passive: true });
    setTimeout(go, 2500);
  } else {
    go();
  }
})();
